import { browser } from '$app/environment';
import posthog from 'posthog-js';

/**
 * @typedef {Object} AnalyticsConfig
 * @property {boolean} posthog - PostHog analytics enabled
 * @property {boolean} sentry - Sentry error tracking enabled  
 * @property {boolean} cloudflare - Cloudflare analytics enabled
 */

/**
 * @typedef {Object} AnalyticsServices
 * @property {Function} initPostHog - Initialize PostHog
 * @property {Function} initSentry - Initialize Sentry
 * @property {Function} initCloudflare - Initialize Cloudflare Analytics
 * @property {Function} destroyAll - Cleanup all services
 */

// Track initialization state
let servicesInitialized = {
	posthog: false,
	sentry: false,
	cloudflare: false
};

/**
 * Initialize PostHog analytics
 */
function initPostHog() {
	if (!browser || servicesInitialized.posthog) return;

	try {
		posthog.init('phc_yjqhRm3qfCi0tJEdxWtTl0lHNrOH0Ukgd7pMsvp7hRl', {
			api_host: 'https://pha.ordalie.tech',
			capture_pageview: false,
			capture_pageleave: false,
			enable_recording_console_log: true
		});
		servicesInitialized.posthog = true;
		console.log('PostHog initialized with consent');
	} catch (error) {
		console.error('Error initializing PostHog:', error);
	}
}

/**
 * Initialize Sentry error tracking (without session replay to minimize cookies)
 */
function initSentry() {
	if (!browser || servicesInitialized.sentry || window.location.hostname.includes('localhost')) return;

	try {
		// Lazy load Sentry to avoid blocking
		if (window.requestIdleCallback) {
			window.requestIdleCallback(() => loadSentryScript());
		} else {
			setTimeout(loadSentryScript, 100);
		}
	} catch (error) {
		console.error('Error initializing Sentry:', error);
	}
}

/**
 * Load Sentry script dynamically
 */
function loadSentryScript() {
	if (servicesInitialized.sentry) return;

	const script = document.createElement('script');
	script.src = 'https://js.sentry-cdn.com/3b27a7b95c52e58d6b8daff7c1b00e50.min.js';
	script.crossOrigin = 'anonymous';
	script.onload = () => {
		if (window.Sentry) {
			const domain = window.location.hostname;
			const environment = domain === 'ordalie.ai' ? 'production' : 
							   domain.includes('staging') ? 'staging' : 'development';

			window.Sentry.init({
				environment,
				replaysSessionSampleRate: 0, // Disabled to minimize cookies
				replaysOnErrorSampleRate: 0  // Disabled to minimize cookies
			});
			servicesInitialized.sentry = true;
			console.log('Sentry initialized with minimal tracking');
		}
	};
	document.head.appendChild(script);
}


/**
 * Initialize Cloudflare Analytics
 */
function initCloudflare() {
	if (!browser || servicesInitialized.cloudflare) return;

	try {
		const script = document.createElement('script');
		script.src = 'https://static.cloudflareinsights.com/beacon.min.js';
		script.defer = true;
		script.setAttribute('data-cf-beacon', '{"token": "d3032d18a80f41e5a6f83d0c04375324"}');
		document.head.appendChild(script);
		
		servicesInitialized.cloudflare = true;
		console.log('Cloudflare Analytics initialized with consent');
	} catch (error) {
		console.error('Error initializing Cloudflare Analytics:', error);
	}
}

/**
 * Initialize all analytics services based on consent
 * @param {AnalyticsConfig} config - Which services to enable
 */
function initializeAnalytics(config = {}) {
	if (!browser) return;

	const {
		posthog = true,
		sentry = true, 
		cloudflare = true
	} = config;

	if (posthog) initPostHog();
	if (sentry) initSentry();
	if (cloudflare) initCloudflare();
}

/**
 * Destroy/cleanup all analytics services
 */
function destroyAllAnalytics() {
	if (!browser) return;

	try {
		// Reset PostHog
		if (servicesInitialized.posthog && window.posthog) {
			posthog.reset();
		}

		// Remove dynamically added scripts
		const scripts = document.querySelectorAll('script[src*="sentry-cdn"], script[src*="cloudflareinsights"]');
		scripts.forEach(script => script.remove());

		// Reset initialization state
		servicesInitialized = {
			posthog: false,
			sentry: false,
			cloudflare: false
		};

		console.log('All analytics services destroyed');
	} catch (error) {
		console.error('Error destroying analytics services:', error);
	}
}

/**
 * Get current initialization status
 * @returns {typeof servicesInitialized}
 */
function getInitializationStatus() {
	return { ...servicesInitialized };
}

// Export analytics management API
export const analytics = {
	initialize: initializeAnalytics,
	destroy: destroyAllAnalytics,
	getStatus: getInitializationStatus,
	
	// Individual service controls
	initPostHog,
	initSentry,
	initCloudflare
};